import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Plus, FileText, Upload, ChevronDown, ChevronRight, PackageCheck, ClipboardList, Paperclip } from "lucide-react";
import { api, API, apiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const n = (v) => Number(v || 0);
const statusColor = (v) => {
  const s = String(v || "").toLowerCase();
  if (["accepted", "issued", "closed"].includes(s)) return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (["submitted", "approved", "partially issued", "need clarification"].includes(s)) return "bg-amber-100 text-amber-700 border-amber-200";
  if (["rejected", "returned / clarification"].includes(s)) return "bg-red-100 text-red-700 border-red-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
};

export default function Requests() {
  const { user } = useAuth();
  const [perm, setPerm] = useState({ can_request: false, can_store_manage: false, can_goods_acceptance: false, allowed_products: [] });
  const [requests, setRequests] = useState([]);
  const [bills, setBills] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [searchParams] = useSearchParams();
  const [matOpen, setMatOpen] = useState(false);
  const [goodsOpen, setGoodsOpen] = useState(false);
  const [matLines, setMatLines] = useState([{ product_id: "", requested_qty: "" }]);
  const [matNotes, setMatNotes] = useState("");
  const [goodsBillId, setGoodsBillId] = useState("");
  const [issueQty, setIssueQty] = useState({});

  const load = async () => {
    try {
      const [r, p] = await Promise.all([api.get("/requests"), api.get("/requests/my-permissions")]);
      setRequests(r.data); setPerm(p.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const o = searchParams.get("open"); if (o) setExpanded(o); }, [searchParams, requests.length]);
  useEffect(() => { if (perm.can_goods_acceptance) api.get("/purchase-bills").then((r) => setBills(r.data)).catch(() => {}); }, [perm.can_goods_acceptance]);

  const products = perm.allowed_products || [];
  const setMatLine = (i, key, val) => setMatLines((prev) => {
    const c = prev.map((l) => ({ ...l }));
    c[i][key] = val;
    if (key === "product_id") { const pr = products.find((x) => x.id === val); if (pr) c[i].unit = pr.unit; }
    return c;
  });

  const submitMatReq = async () => {
    const valid = matLines.filter((l) => l.product_id && n(l.requested_qty) > 0);
    if (!valid.length) { toast.error("Add at least one product with quantity"); return; }
    const items = valid.map((l) => { const pr = products.find((x) => x.id === l.product_id) || {}; return { product_id: l.product_id, product_name: pr.product_name, grade: pr.grade, unit: pr.unit || "MT", requested_qty: n(l.requested_qty) }; });
    try {
      await api.post("/requests", { type: "material_issue", items, notes: matNotes });
      toast.success("Material request submitted");
      setMatOpen(false); setMatLines([{ product_id: "", requested_qty: "" }]); setMatNotes(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const submitGoods = async () => {
    const bill = bills.find((b) => b.id === goodsBillId);
    if (!bill) { toast.error("Select a purchase bill"); return; }
    const items = (bill.items || []).map((it) => ({ product_id: it.product_id, product_name: it.product_name, grade: it.grade, batch_number: it.batch_number, requested_qty: it.quantity, unit: it.unit }));
    try {
      await api.post("/requests", { type: "goods_acceptance", purchase_bill_id: bill.id, purchase_bill_no: bill.bill_no, supplier: bill.supplier_name, warehouse: bill.warehouse, purchase_date: bill.bill_date, attachments: bill.attachments || [], items, notes: `Goods received against Bill ${bill.bill_no}` });
      toast.success("Goods acceptance request sent to Store");
      setGoodsOpen(false); setGoodsBillId(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const doAction = async (req, action, issued_items = null, remarks = null) => {
    try { await api.post(`/requests/${req.id}/action`, { action, issued_items, remarks }); toast.success(`Request ${action}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const issueFull = (req) => doAction(req, "issue", req.items.map((it) => ({ product_id: it.product_id, issued_qty: it.requested_qty })));
  const issuePartial = (req) => doAction(req, "partial", req.items.map((it) => ({ product_id: it.product_id, issued_qty: n(issueQty[`${req.id}_${it.product_id}`]) })));

  const uploadSigned = async (req, file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/files/upload?owner_module=requests&kind=signed_slip`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/requests/${req.id}/signed-slip`, { attachment: data });
      toast.success("Signed slip uploaded"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openSlip = (id) => window.open(`${API}/requests/${id}/issue-slip-pdf?token=${localStorage.getItem("token")}`, "_blank");

  const storeActionable = (req) => perm.can_store_manage && (req.type === "goods_acceptance"
    ? req.status === "Submitted"
    : ["Submitted", "Need Clarification", "Approved", "Partially Issued"].includes(req.status));
  const mine = (req) => req.requested_by_user_id === user?.id;
  const canUploadSlip = (req) => user?.role === "admin" || user?.role === (req.to_department || "store");

  const heading = perm.can_store_manage ? "Store Inbox" : "My Requests";

  return (
    <div data-testid="requests-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div><h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head flex items-center gap-2"><ClipboardList className="w-6 h-6" /> Requests</h1>
          <p className="text-sm text-slate-500 mt-0.5">{heading} · {requests.length} record{requests.length !== 1 ? "s" : ""}</p></div>
        <div className="flex items-center gap-2 flex-wrap">
          {perm.can_goods_acceptance && <Button data-testid="new-goods-btn" onClick={() => setGoodsOpen(true)} variant="outline" className="rounded-sm gap-1.5"><PackageCheck className="w-4 h-4" /> Goods Acceptance</Button>}
          {perm.can_request && <Button data-testid="new-material-request-btn" onClick={() => setMatOpen(true)} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5"><Plus className="w-4 h-4" /> Material Request</Button>}
        </div>
      </div>

      {!perm.can_request && !perm.can_store_manage && requests.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-10 text-center text-slate-400" data-testid="requests-empty">You do not have request permissions. Contact your administrator to enable requests.</div>
      )}

      <div className="space-y-3">
        {requests.map((req) => {
          const isOpen = expanded === req.id;
          return (
            <div key={req.id} className="bg-white border border-slate-200 rounded-sm" data-testid={`request-${req.id}`}>
              <button onClick={() => setExpanded(isOpen ? null : req.id)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
                <div className="flex items-center gap-3 min-w-0">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800 truncate">{req.request_no} · <span className="text-slate-500 text-sm">{req.type === "goods_acceptance" ? "Goods Acceptance" : "Material Request"}</span></div>
                    <div className="text-xs text-slate-500">By {req.requested_by_name} ({req.requested_by_role}) · {fmtDate(req.created_at)}{req.purchase_bill_no ? ` · Bill ${req.purchase_bill_no}` : ""}</div>
                  </div>
                </div>
                <Badge variant="outline" className={`rounded-full font-medium px-2.5 shrink-0 ${statusColor(req.status)}`} data-testid={`request-status-${req.id}`}>{req.status}</Badge>
              </button>

              {isOpen && (
                <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-slate-500 text-xs"><th className="py-1 pr-3">Product</th><th className="py-1 pr-3">Grade</th><th className="py-1 pr-3">Batch</th><th className="py-1 pr-3">Requested</th><th className="py-1 pr-3">Issued</th><th className="py-1 pr-3">Unit</th>{storeActionable(req) && req.type === "material_issue" && <th className="py-1 pr-3">Issue Qty</th>}</tr></thead>
                      <tbody>
                        {(req.items || []).map((it, i) => (
                          <tr key={i} className="border-t border-slate-50">
                            <td className="py-1.5 pr-3 text-slate-800">{it.product_name}</td>
                            <td className="py-1.5 pr-3 text-slate-600">{it.grade || "-"}</td>
                            <td className="py-1.5 pr-3 text-slate-600">{it.batch_number || "-"}</td>
                            <td className="py-1.5 pr-3 font-mono">{n(it.requested_qty)}</td>
                            <td className="py-1.5 pr-3 font-mono">{n(it.issued_qty)}</td>
                            <td className="py-1.5 pr-3 text-slate-600">{it.unit}</td>
                            {storeActionable(req) && req.type === "material_issue" && (
                              <td className="py-1.5 pr-3"><Input data-testid={`issue-qty-${req.id}-${it.product_id}`} type="number" className="h-7 w-24 bg-white" value={issueQty[`${req.id}_${it.product_id}`] ?? ""} onChange={(e) => setIssueQty((p) => ({ ...p, [`${req.id}_${it.product_id}`]: e.target.value }))} /></td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {(req.attachments || []).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {req.attachments.map((a, i) => a.id && (
                        <a key={i} href={`${API}/files/download/${a.id}?token=${localStorage.getItem("token")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#0B132B] bg-slate-50 rounded-sm px-2 py-1 hover:underline"><Paperclip className="w-3 h-3" /> {a.original_filename || a.kind || "attachment"}</a>
                      ))}
                    </div>
                  )}

                  {req.notes && <div className="text-sm text-slate-600"><span className="font-semibold">Notes:</span> {req.notes}</div>}

                  <div className="border border-slate-100 rounded-sm p-2">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Timeline</div>
                    <div className="space-y-1">
                      {(req.timeline || []).map((t, i) => (
                        <div key={i} className="text-xs text-slate-600 flex items-center gap-2"><span className="font-medium text-slate-800 w-40 shrink-0">{t.action}</span><span>{t.user_name} ({t.role})</span><span className="text-slate-400">{new Date(t.at).toLocaleString()}</span>{t.remarks && <span className="italic text-slate-400">— {t.remarks}</span>}</div>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {req.type === "goods_acceptance" && req.purchase_bill_id && (
                      <Button data-testid={`bill-pdf-${req.id}`} size="sm" variant="outline" onClick={() => window.open(`${API}/pdf/purchase_bill/${req.purchase_bill_id}?token=${localStorage.getItem("token")}`, "_blank")} className="rounded-sm gap-1.5"><FileText className="w-4 h-4" /> View Bill PDF</Button>
                    )}
                    {storeActionable(req) && req.type === "goods_acceptance" && (
                      <>
                        <Button data-testid={`accept-${req.id}`} size="sm" onClick={() => doAction(req, "accept")} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">Accept</Button>
                        <Button data-testid={`reject-${req.id}`} size="sm" variant="outline" onClick={() => doAction(req, "reject")} className="rounded-sm text-red-600">Reject</Button>
                        <Button data-testid={`return-${req.id}`} size="sm" variant="outline" onClick={() => doAction(req, "return")} className="rounded-sm">Return / Clarify</Button>
                      </>
                    )}
                    {storeActionable(req) && req.type === "material_issue" && (
                      <>
                        <Button data-testid={`issue-full-${req.id}`} size="sm" onClick={() => issueFull(req)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">Issue Full</Button>
                        <Button data-testid={`issue-partial-${req.id}`} size="sm" variant="outline" onClick={() => issuePartial(req)} className="rounded-sm">Issue Partial</Button>
                        <Button data-testid={`req-reject-${req.id}`} size="sm" variant="outline" onClick={() => doAction(req, "reject")} className="rounded-sm text-red-600">Reject</Button>
                        <Button data-testid={`req-clarify-${req.id}`} size="sm" variant="outline" onClick={() => doAction(req, "clarify")} className="rounded-sm">Clarify</Button>
                      </>
                    )}
                    {req.type === "material_issue" && req.issued_by_name && (
                      <Button data-testid={`slip-pdf-${req.id}`} size="sm" variant="outline" onClick={() => openSlip(req.id)} className="rounded-sm gap-1.5"><FileText className="w-4 h-4" /> Issue Slip PDF</Button>
                    )}
                    {canUploadSlip(req) && req.type === "material_issue" && ["Issued", "Partially Issued"].includes(req.status) && (
                      <label className="inline-flex items-center gap-1.5 text-sm text-[#0B132B] cursor-pointer border border-slate-200 rounded-sm px-2.5 py-1.5 hover:bg-slate-50" data-testid={`upload-signed-${req.id}`}>
                        <Upload className="w-4 h-4" /> Upload Signed Slip
                        <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={(e) => uploadSigned(req, e.target.files?.[0])} />
                      </label>
                    )}
                    {(req.signed_slips || []).map((a, i) => a.id && (
                      <a key={i} href={`${API}/files/download/${a.id}?token=${localStorage.getItem("token")}`} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 bg-emerald-50 rounded-sm px-2 py-1 hover:underline">Signed: {a.original_filename || "slip"}</a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Material Request dialog */}
      <Dialog open={matOpen} onOpenChange={setMatOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-head">New Material Request</DialogTitle><DialogDescription>Request materials from Store. You can only request products your administrator has permitted.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            {matLines.map((l, i) => (
              <div key={i} className="flex items-center gap-2">
                <Select value={l.product_id || ""} onValueChange={(v) => setMatLine(i, "product_id", v)}>
                  <SelectTrigger data-testid={`mat-line-${i}-product`} className="bg-white flex-1"><SelectValue placeholder="Select product" /></SelectTrigger>
                  <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.product_name}{p.grade ? ` (${p.grade})` : ""}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid={`mat-line-${i}-qty`} type="number" placeholder="Qty" className="w-28 bg-white" value={l.requested_qty} onChange={(e) => setMatLine(i, "requested_qty", e.target.value)} />
                <span className="text-xs text-slate-500 w-10">{l.unit || ""}</span>
                {matLines.length > 1 && <button onClick={() => setMatLines(matLines.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-600 text-sm">✕</button>}
              </div>
            ))}
            {products.length === 0 && <div className="text-sm text-amber-600">No products are permitted for you yet. Ask your administrator.</div>}
            <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => setMatLines([...matLines, { product_id: "", requested_qty: "" }])}><Plus className="w-3.5 h-3.5" /> Add Product</Button>
            <Textarea data-testid="mat-notes" placeholder="Notes (optional)" value={matNotes} onChange={(e) => setMatNotes(e.target.value)} className="bg-white mt-2" />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setMatOpen(false)} className="rounded-sm">Cancel</Button><Button data-testid="submit-material-request" onClick={submitMatReq} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm">Submit to Store</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Goods Acceptance dialog */}
      <Dialog open={goodsOpen} onOpenChange={setGoodsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-head">Goods Acceptance Request</DialogTitle><DialogDescription>Send a purchase bill to Store for goods acceptance.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Purchase Bill</label>
            <Select value={goodsBillId} onValueChange={setGoodsBillId}>
              <SelectTrigger data-testid="goods-bill-select" className="bg-white"><SelectValue placeholder="Select purchase bill" /></SelectTrigger>
              <SelectContent>{bills.filter((b) => String(b.status || "").toLowerCase() !== "cancelled").map((b) => <SelectItem key={b.id} value={b.id}>{b.bill_no} · {b.supplier_name} · {fmtDate(b.bill_date)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setGoodsOpen(false)} className="rounded-sm">Cancel</Button><Button data-testid="submit-goods-request" onClick={submitGoods} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm">Send to Store</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
